import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { claimChunkReload, isChunkLoadError } from './chunkLoadRecovery';

// #915 — route splitting means a navigation can now fail if its chunk cannot
// be fetched. The common cause in production is a deploy race: an open tab
// holds an old index.html, the new deploy has replaced the hashed chunk, and
// the dynamic import 404s. In a data router that rejection is handled by the
// route's `errorElement`, NOT the app-level React ErrorBoundary, so without
// this the reviewer sees React Router's default error screen.

interface RouteErrorPanelProps {
  error?: unknown;
  onReload: () => void;
}

/** Presentational error screen — pure, so it can be rendered directly in tests. */
export function RouteErrorPanel({ error, onReload }: RouteErrorPanelProps) {
  const staleDeploy = isChunkLoadError(error);
  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6">
      <div
        role="alert"
        className="bg-navy-light border border-navy-lighter rounded-xl p-8 max-w-md text-center"
      >
        <h2 className="text-lg font-semibold text-white mb-2">
          {staleDeploy ? 'A new version is available' : 'This page failed to load'}
        </h2>
        <p className="text-sm text-gray-400 mb-2">
          {staleDeploy
            ? 'The app was updated while this tab was open. Reload to pick up the latest version.'
            : 'Something went wrong loading this screen. Reloading usually fixes it.'}
        </p>
        {error instanceof Error && error.message && (
          <p className="text-xs text-gray-500 font-mono mb-4 break-all">
            {error.message}
          </p>
        )}
        <button
          type="button"
          onClick={onReload}
          className="px-4 py-2 bg-amber hover:bg-amber-hover text-navy text-sm font-semibold rounded"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

/**
 * Router `errorElement`. Detects a stale-deploy chunk failure and reloads the
 * tab once automatically (guarded against a reload loop); anything else gets
 * the manual reload screen.
 */
export function RouteError() {
  const error = useRouteError();

  useEffect(() => {
    if (isChunkLoadError(error) && claimChunkReload()) {
      window.location.reload();
    }
  }, [error]);

  return <RouteErrorPanel error={error} onReload={() => window.location.reload()} />;
}
